import React from 'react';
import { useLingui } from '@lingui/react/macro';

import type { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { AgeEnum } from '@rover/rsdk/src/apiClient/latest/model/ageEnum';
import type { LocalizedBrandServicePetType } from '@rover/rsdk/src/apiClient/latest/model/localizedBrandServicePetType';
import type { PaginatedServiceTypeList } from '@rover/rsdk/src/apiClient/latest/model/paginatedServiceTypeList';
import type { PetDetails } from '@rover/rsdk/src/apiClient/latest/model/petDetails';
import { PetTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/petTypeEnum';
import type { ServiceType } from '@rover/rsdk/src/apiClient/latest/model/serviceType';

import LandingHero, { HeroImageSrcSet } from '../../components/LandingHero';
import type { LandingSearchFormFilters } from '../../components/LandingSearchForm';
import catHeroImage from '../pages/CatBoarding/images/cat-boarding-hero-desktop.png';
import dogBoardingHeroDesktop from '../pages/DogBoarding/images/dog-boarding-hero-desktop.jpg';
import dogBoardingHeroTablet from '../pages/DogBoarding/images/dog-boarding-hero-tablet.jpg';
import doggyDayCareHeroImage from '../pages/DoggyDayCare/images/doggy-daycare-hero-desktop.jpg';
import dogTrainingHeroDesktop from '../pages/DogTraining/images/training-hero-desktop.jpg';
import dogTrainingHeroTablet from '../pages/DogTraining/images/training-hero-tablet.jpg';
import dogWalkingHeroDesktop from '../pages/DogWalking/images/dog-walking-hero-desktop.jpg';
import dogWalkingHeroTablet from '../pages/DogWalking/images/dog-walking-hero-tablet.jpg';
import houseSittingHeroDesktop from '../pages/HouseSitting/images/house-sitting-hero-desktop.jpg';
import houseSittingHeroTablet from '../pages/HouseSitting/images/house-sitting-hero-tablet.jpg';
import petSittingHeroDesktop from '../pages/PetSitting/images/pet-sitting-hero-desktop.jpg';
import petSittingHeroTablet from '../pages/PetSitting/images/pet-sitting-hero-tablet.jpg';
import puppyHeroDesktop from '../pages/shared-images/Hero_Puppy_desktop.jpg';
import puppyHeroTablet from '../pages/shared-images/Hero_Puppy_tablet.jpg';

type Props = {
  h1: string;
  h2: string;
  locale: Locale;
  serviceTypes: PaginatedServiceTypeList;
  internalServiceType: Pick<ServiceType, 'slug' | 'providerNounPlural'> &
    Partial<Pick<ServiceType, 'startDateSelectorDescription' | 'endDateSelectorDescription'>>;
  initialSearchFilters: LandingSearchFormFilters;
  petType: LocalizedBrandServicePetType | undefined;
  age: PetDetails['age'];
  internalServiceTypeSlug: string;
};

const CAT_HERO_IMAGES: HeroImageSrcSet = {
  desktop: { src: catHeroImage, height: 500 },
  tablet: { src: catHeroImage, height: 500 },
};

const PUPPY_HERO_IMAGES: HeroImageSrcSet = {
  desktop: { src: puppyHeroDesktop, height: 500 },
  tablet: { src: puppyHeroTablet, height: 500 },
};

const DOG_BOARDING_HERO_IMAGES: HeroImageSrcSet = {
  desktop: { src: dogBoardingHeroDesktop, height: 500 },
  tablet: { src: dogBoardingHeroTablet, height: 500 },
};

const DOG_WALKING_HERO_IMAGES: HeroImageSrcSet = {
  desktop: { src: dogWalkingHeroDesktop, height: 500 },
  tablet: { src: dogWalkingHeroTablet, height: 500 },
};

const DOGGY_DAY_CARE_HERO_IMAGES: HeroImageSrcSet = {
  desktop: { src: doggyDayCareHeroImage, height: 500 },
  tablet: { src: doggyDayCareHeroImage, height: 500 },
};

const HOUSE_SITTING_HERO_IMAGES: HeroImageSrcSet = {
  desktop: { src: houseSittingHeroDesktop, height: 500 },
  tablet: { src: houseSittingHeroTablet, height: 500 },
};

const PET_SITTING_HERO_IMAGES: HeroImageSrcSet = {
  desktop: { src: petSittingHeroDesktop, height: 500 },
  tablet: { src: petSittingHeroTablet, height: 500 },
};

const DOG_TRAINING_HERO_IMAGES: HeroImageSrcSet = {
  desktop: { src: dogTrainingHeroDesktop, height: 500 },
  tablet: { src: dogTrainingHeroTablet, height: 500 },
};

// `internalServiceType.slug` values with their own hero art; anything else (chiefly the most
// common case, 'overnight-boarding') falls through to DOG_BOARDING_HERO_IMAGES below.
const HERO_IMAGES_BY_INTERNAL_SERVICE_TYPE: Record<string, HeroImageSrcSet> = {
  'dog-walking': DOG_WALKING_HERO_IMAGES,
  'overnight-traveling': HOUSE_SITTING_HERO_IMAGES,
  'drop-in': PET_SITTING_HERO_IMAGES,
  'doggy-day-care': DOGGY_DAY_CARE_HERO_IMAGES,
  'dog-training': DOG_TRAINING_HERO_IMAGES,
};

// Selects the hero image, mirroring SeoLocationHero's own getHeroImage: pet type/age are checked
// first for the two cases with a distinct look regardless of service type (cat, and puppy via
// the adolescent age band), then the image follows the internal service type, falling back to
// dog boarding.
export const getServiceLandingHeroImage = (
  petType: LocalizedBrandServicePetType | undefined,
  age: PetDetails['age'],
  internalServiceTypeSlug: string
): HeroImageSrcSet => {
  if (petType === PetTypeEnum.cat) {
    return CAT_HERO_IMAGES;
  }
  if (petType === PetTypeEnum.dog && age === AgeEnum.adolescent) {
    return PUPPY_HERO_IMAGES;
  }
  return HERO_IMAGES_BY_INTERNAL_SERVICE_TYPE[internalServiceTypeSlug] ?? DOG_BOARDING_HERO_IMAGES;
};

const ServiceLandingHero: React.FC<Props> = ({
  h1,
  h2,
  locale,
  serviceTypes,
  internalServiceType,
  initialSearchFilters,
  petType,
  age,
  internalServiceTypeSlug,
}) => {
  const { t } = useLingui();
  const { providerNounPlural } = internalServiceType;

  // i18n: providerNounPlural is a plural noun naming this page's provider type
  // (e.g. "dog walkers", "house sitters", "pet sitters", "dog boarders")
  const submitButtonText = t`Find trusted ${providerNounPlural}`;

  return (
    <LandingHero
      heroImageSrcSet={getServiceLandingHeroImage(petType, age, internalServiceTypeSlug)}
      upliftedContent
    >
      <LandingHero.Content upliftedContent>
        <LandingHero.Display>{h1}</LandingHero.Display>
        <LandingHero.Text>{h2}</LandingHero.Text>
        <LandingHero.SearchForm
          locale={locale}
          serviceTypes={serviceTypes}
          internalServiceType={internalServiceType}
          initialSearchFilters={initialSearchFilters}
          showSearchFilters={false}
          submitButtonText={submitButtonText}
        />
      </LandingHero.Content>
    </LandingHero>
  );
};

export default ServiceLandingHero;
